import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Modal, Select } from 'antd';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { Template } from '@totem/components/ticketing/templateManagement/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull, isNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT, TICKETS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const AddTasksFromTemplateButton = () => {
  const { getApiToken } = useContext(UserProfileContext);
  const { ticket, onAction } = useContext(TicketContext);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);

  const fetchTemplates = useCallback(() => {
    setLoading(true);

    fetch(`${TICKETS_ENDPOINT}/templates`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: Template[]) => {
        if (isNotNull(result)) {
          setTemplates(result);
        } else {
          setTemplates([]);
        }
      })
      .finally(() => setLoading(false));
  }, [getApiToken]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleTemplateSelected = (template: Template) => {
    if (!ticket?.ticket?.id) {
      return;
    }
    setIsSaving(true);

    fetch(
      `${TICKET_ENDPOINT}/${ticket.ticket.id}/tasks/template/${template.id}`,
      {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      setIsSaving(false);
      const success = CheckResponseShowError(res);
      if (success) {
        setShowModal(false);

        setSelectedTemplateId(null);

        onAction('refresh', '');
      }
    });
  };

  const handleOk = () => {
    if (!selectedTemplateId) {
      return;
    }
    const template = templates.find((item) => item.id === selectedTemplateId);
    if (template) {
      handleTemplateSelected(template);
    }
  };

  const handleCancel = () => {
    setShowModal(false);

    setSelectedTemplateId(null);
  };

  const templateOptions = templates.map((item) => ({
    value: item.id,
    label: item.name
      ? `${item.name} (${item.category} / ${item.subCategory})`
      : `${item.category} / ${item.subCategory}`,
  }));

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        disabled={isNull(templates) || templates.length === 0}
        loading={loading}
      >
        Add Tasks from Template
      </Button>

      <Modal
        title="Select Template"
        open={showModal}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Add Tasks"
        okButtonProps={{ disabled: !selectedTemplateId }}
        confirmLoading={isSaving}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select a template"
          value={selectedTemplateId}
          onChange={(value) => setSelectedTemplateId(value)}
          options={templateOptions}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      </Modal>
    </>
  );
};

export default AddTasksFromTemplateButton;
